<script setup>
import { computed } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({
  lead: { type: Object, required: true },
  // Posição do estágio no pipeline (vindas da coluna) - base do progresso.
  stageIndex: { type: Number, default: 0 },
  stageCount: { type: Number, default: 0 },
});

const emit = defineEmits(['click', 'dragstart', 'dragend']);

const valueFormatted = computed(() => {
  if (!props.lead.value_cents) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: props.lead.currency || 'BRL',
  }).format(props.lead.value_cents / 100);
});

// Classificação define a faixa de accent (esquerda) e o chip.
const CLASS_META = {
  hot: { label: 'Quente', chip: 'bg-n-ruby-3 text-n-ruby-11', accent: 'bg-n-ruby-9' },
  warm: { label: 'Morno', chip: 'bg-n-amber-3 text-n-amber-11', accent: 'bg-n-amber-9' },
  cold: { label: 'Frio', chip: 'bg-n-blue-3 text-n-blue-11', accent: 'bg-n-blue-9' },
};
const cls = computed(
  () =>
    CLASS_META[props.lead.classification] || {
      label: '-',
      chip: 'bg-n-alpha-2 text-n-slate-11',
      accent: 'bg-n-slate-6',
    }
);

const daysIdle = computed(() => {
  const t = props.lead.last_activity_at;
  if (!t) return 0;
  return Math.floor((Date.now() - new Date(t).getTime()) / 86400000);
});

const idleClass = computed(() => {
  const d = daysIdle.value;
  if (d > 14) return 'text-n-ruby-11';
  if (d > 7) return 'text-n-amber-11';
  return 'text-n-slate-10';
});

// Badge de data: vencido → ruby, perto (≤3d) → amber, ok → grass.
const dueMeta = computed(() => {
  const raw = props.lead.expected_close_date;
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  const diffDays = Math.ceil((date.getTime() - Date.now()) / 86400000);
  const label = date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
  let chip = 'bg-n-grass-3 text-n-grass-11';
  if (diffDays < 0) chip = 'bg-n-ruby-3 text-n-ruby-11';
  else if (diffDays <= 3) chip = 'bg-n-amber-3 text-n-amber-11';
  return { label, chip };
});

const summary = computed(() => (props.lead.summary || '').trim());

// Progresso = POSIÇÃO DO LEAD NO PIPELINE (estágio atual / total de estágios).
// Ex.: 1º de 4 estágios = 25%. É o que o usuário espera ver no card do Kanban
// (a barra acompanha a coluna). Quando não há contexto de estágios (ex.: fora
// do board), cai para o status do ciclo de vida do lead.
const STATUS_META = {
  draft: { label: 'Rascunho', pct: 20, bar: 'bg-n-slate-9' },
  qualified: { label: 'Qualificado', pct: 45, bar: 'bg-n-blue-9' },
  in_pipeline: { label: 'Em pipeline', pct: 70, bar: 'bg-n-brand' },
  won: { label: 'Ganho', pct: 100, bar: 'bg-n-grass-9' },
  lost: { label: 'Perdido', pct: 100, bar: 'bg-n-ruby-9' },
  discarded: { label: 'Descartado', pct: 100, bar: 'bg-n-slate-7' },
};
const progress = computed(() => {
  if (props.stageCount > 0) {
    const position = props.stageIndex + 1;
    const pct = Math.round((position / props.stageCount) * 100);
    const stageName = props.lead.stage?.name;
    return {
      label: stageName
        ? `${stageName} · ${position}/${props.stageCount}`
        : `Estágio ${position} de ${props.stageCount}`,
      pct,
      bar: 'bg-n-brand',
    };
  }
  return STATUS_META[props.lead.status] || STATUS_META.draft;
});

// Etiquetas do lead (até 3 + contador) - "mais detalhes" reais.
const labels = computed(() => {
  const list = Array.isArray(props.lead.labels) ? props.lead.labels : [];
  return { shown: list.slice(0, 3), extra: Math.max(0, list.length - 3) };
});
</script>

<template>
  <div
    class="group relative rounded-xl bg-n-solid-1 border border-n-weak hover:border-n-slate-7 hover:shadow-sm transition-all cursor-grab active:cursor-grabbing overflow-hidden"
    draggable="true"
    @dragstart="emit('dragstart', $event)"
    @dragend="emit('dragend', $event)"
    @click="emit('click')"
  >
    <!-- Faixa de accent (cor da classificação) -->
    <span class="absolute inset-y-0 left-0 w-1" :class="cls.accent" />

    <div class="pl-3.5 pr-2.5 py-2.5 flex flex-col gap-1.5">
      <!-- Título + classificação -->
      <div class="flex items-start justify-between gap-2">
        <p class="text-body-main font-medium text-n-slate-12 truncate mb-0">
          {{ lead.contact?.name || 'Sem nome' }}
        </p>
        <span
          class="shrink-0 text-label-small px-1.5 py-0.5 rounded uppercase font-semibold"
          :class="cls.chip"
        >
          {{ cls.label }}
        </span>
      </div>

      <!-- Subtítulo: pipeline · intenção -->
      <p class="text-body-small text-n-slate-11 truncate mb-0">
        {{ lead.pipeline?.name || 'Sem pipeline' }}
        <span v-if="lead.lead_intent_name"> · {{ lead.lead_intent_name }}</span>
      </p>

      <!-- Etiquetas do lead -->
      <div
        v-if="labels.shown.length"
        class="flex items-center gap-1 flex-wrap"
      >
        <span
          v-for="l in labels.shown"
          :key="l"
          class="text-label-small px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11 truncate max-w-[7rem]"
        >
          {{ l }}
        </span>
        <span
          v-if="labels.extra"
          class="text-label-small px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11"
        >
          +{{ labels.extra }}
        </span>
      </div>

      <!-- Resumo: destacado quando gerado pela IA -->
      <div
        v-if="summary"
        class="rounded-md px-2 py-1.5"
        :class="lead.ai_qualified ? 'bg-n-brand/5 border border-n-brand/15' : 'bg-n-alpha-1'"
      >
        <span
          v-if="lead.ai_qualified"
          class="flex items-center gap-1 text-label-small text-n-brand font-medium mb-0.5"
        >
          <i class="i-lucide-sparkles size-3" />
          Resumo da IA
        </span>
        <p class="text-body-small text-n-slate-11 line-clamp-3 mb-0">
          {{ summary }}
        </p>
      </div>

      <!-- Meta: valor · time · IA -->
      <div
        class="flex items-center gap-3 text-label-small text-n-slate-11 flex-wrap"
      >
        <span
          v-if="valueFormatted"
          class="inline-flex items-center gap-1 font-medium text-n-slate-12"
        >
          <i class="i-lucide-banknote size-3" />
          {{ valueFormatted }}
        </span>
        <span
          v-if="lead.team_name"
          class="inline-flex items-center gap-1"
        >
          <i class="i-lucide-users size-3" />
          {{ lead.team_name }}
        </span>
        <span
          v-if="lead.ai_qualified"
          class="inline-flex items-center gap-1 text-n-brand"
        >
          <i class="i-lucide-sparkles size-3" />
          IA
        </span>
      </div>

      <!-- Progresso no ciclo (status do lead) -->
      <div class="mt-0.5">
        <div class="flex items-center justify-between mb-1">
          <span class="text-label-small text-n-slate-11">{{ progress.label }}</span>
          <span class="text-label-small text-n-slate-10">{{ progress.pct }}%</span>
        </div>
        <div class="h-1.5 rounded-full bg-n-alpha-2 overflow-hidden">
          <div
            class="h-full rounded-full transition-all"
            :class="progress.bar"
            :style="{ width: `${progress.pct}%` }"
          />
        </div>
      </div>

      <!-- Rodapé: responsável · dias sem ação · data prevista -->
      <div
        class="flex items-center justify-between pt-1.5 mt-0.5 border-t border-n-weak/60"
      >
        <div class="flex items-center gap-1.5 min-w-0">
          <Avatar
            v-if="lead.owner"
            :name="lead.owner.name"
            :src="lead.owner.avatar_url"
            :size="20"
            class="rounded-full shrink-0"
          />
          <span
            v-else
            class="flex justify-center items-center font-semibold rounded-full size-5 shrink-0 bg-n-alpha-2 text-n-slate-10 text-label-small"
          >
            -
          </span>
          <span class="text-label-small text-n-slate-11 truncate">
            {{ lead.owner?.name || 'Sem responsável' }}
          </span>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <span class="text-label-small" :class="idleClass">
            {{ daysIdle }}d
          </span>
          <span
            v-if="dueMeta"
            class="text-label-small px-1.5 py-0.5 rounded font-medium"
            :class="dueMeta.chip"
          >
            {{ dueMeta.label }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
